import React, { useState, useEffect } from 'react';
import useInstallPrompt from '../../hooks/useInstallPrompt';
import usePageViewCount from '../../hooks/usePageViewCount';
import { useAnalytics } from '../../analytics/hooks';
import config from '../../config';
import '../../styles/install-banner.css';

const DISMISS_KEY = 'pwa_banner_dismissed_at';

/**
 * Promotional banner encouraging PWA install.
 * Appears on Nth page view (config-driven), suppressed 30 days after dismiss.
 */
const InstallBanner = () => {
  const { isInstallable, isInstalled, isIOSSafari, promptInstall } = useInstallPrompt();
  const { pageViewCount } = usePageViewCount();
  const { track } = useAnalytics();

  const [isDismissed, setIsDismissed] = useState(() => {
    const ts = localStorage.getItem(DISMISS_KEY);
    if (!ts) return false;
    const daysSince = (Date.now() - parseInt(ts, 10)) / (1000 * 60 * 60 * 24);
    return daysSince < config.pwa.bannerDismissDays;
  });

  const shouldShow =
    (isInstallable || isIOSSafari) &&
    !isInstalled &&
    pageViewCount >= config.pwa.bannerPageViewThreshold &&
    !isDismissed;

  useEffect(() => {
    if (shouldShow) {
      track('install_banner_shown');
    }
  }, [shouldShow, track]);

  if (!shouldShow) return null;

  const handleInstall = () => {
    track('install_banner_clicked');
    promptInstall();
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setIsDismissed(true);
    track('install_banner_dismissed');
  };

  return (
    <div className="install-banner" role="complementary" aria-label="Install application">
      <p className="install-banner__text">
        Install JyotishSastra for faster access and offline charts
      </p>
      <button className="install-banner__install" onClick={handleInstall} type="button">
        Install
      </button>
      <button
        className="install-banner__dismiss"
        onClick={handleDismiss}
        type="button"
        aria-label="Dismiss"
      >
        &#x2715;
      </button>
    </div>
  );
};

export default InstallBanner;
